import { h, Fragment } from 'preact';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useEffect, useState } from 'preact/hooks';
import { Navigation } from 'swiper';
import AngleRightIcon from 'utils/icon/icon-angle-right.svg';
import clsx from 'clsx';

const Swatch = ({
  product,
  currentVariant,
  variant,
  productId,
  colorOptionIndex,
  handleVariantChange,
  condensed,
  template,
  prefix = 'swatch',
}) => {
  const colorOption = variant.selectedOptions.find((option) => option.name.toLowerCase() === 'color');
  if (!colorOption) return null;

  const available = product.variants.some((productVariant) =>
    productVariant.selectedOptions.some(
      (option) => option.value === colorOption.value && productVariant.availableForSale,
    ),
  );

  const getHexCodeWithHash = (hexCode) => (hexCode?.startsWith('#') ? hexCode : `#${hexCode}`);

  const bgColorVar = getHexCodeWithHash(variant.hexCode || '#ccc');
  const className = condensed ? 'h-25 w-25 min-w-25' : 'h-[30px] w-[30px] min-w-[30px]';
  const variantId = variant.id.split('/ProductVariant/')[1];

  return (
    <div
      key={variant.id}
      className={clsx(
        'relative flex cursor-pointer items-center rounded-full border border-transparent',
        {
          'ring-1 ring-grey-5': bgColorVar.toLowerCase() === '#ffffff',
          '!cursor-default after:absolute after:z-10 after:w-1 after:rotate-[45deg] after:bg-black after:content-[""]':
            !available,
          'after:-top-[3px] after:left-[11px] after:h-[30px]': condensed,
          'after:-top-[2px] after:left-[14px] after:h-[34px]': !condensed,
        },
        className,
      )}
      style={{ backgroundColor: bgColorVar }}
      onTouchStart={(e) => {
        e.preventDefault();
        const input = document.getElementById(`${prefix}-${variantId}-${template}`);
        if (input && !input.disabled) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }}
    >
      <input
        checked={
          currentVariant?.selectedOptions?.[colorOptionIndex]?.value?.toLowerCase() ===
          colorOption?.value?.toLowerCase()
        }
        id={`${prefix}-${variantId}-${template}`}
        onChange={(e) => handleVariantChange(e, variantId, colorOption)}
        type="radio"
        value={colorOption.value}
        name={`${prefix}-${productId}-color`}
        data-option-name="Color"
        className={`swatch ${className} ${!available ? 'disabled' : ''}`}
        disabled={!available}
      />
      <label htmlFor={`${prefix}-${variantId}-${template}`}>
        <span className="sr-only">{colorOption.value}</span>
      </label>
    </div>
  );
};

const Swatches = ({
  product,
  currentVariant,
  setCurrentVariant,
  handleVariantChange,
  condensed,
  isDesktop = null,
  template,
  prefix = '',
  filteredVariants = [],
}) => {
  const productId = product.idClean;
  const colorOptionIndex = product.options?.findIndex((o) => o.name.toLowerCase().includes('color'));

  const [colorVariants, setColorVariants] = useState(
    () =>
      product?.variants.filter(
        (variant, index, self) =>
          index ===
          self.findIndex(
            (otherVariant) =>
              otherVariant.selectedOptions[colorOptionIndex]?.value ===
              variant.selectedOptions[colorOptionIndex]?.value,
          ),
      ) || [],
  );

  useEffect(() => {
    if (template === 'pdp' || template === 'collection') {
      const colors = [...new Set(filteredVariants.map((item) => item.selectedOptions[colorOptionIndex]?.value))];
      const updatedColorVariants = colorVariants.filter((item) =>
        colors.includes(item.selectedOptions[colorOptionIndex]?.value),
      );

      if (JSON.stringify(updatedColorVariants) !== JSON.stringify(colorVariants)) {
        setColorVariants(updatedColorVariants);
      }
    }
  }, [filteredVariants, colorVariants, colorOptionIndex, setCurrentVariant, template]);

  if (colorOptionIndex < 0) {
    return (
      <div className="mb-12 h-25">
        <div
          className={`relative flex h-25 w-25 cursor-pointer items-center
            rounded-full border border-transparent ring-1 ring-grey-5`}
        >
          <div
            className={`swatch bg-putty ring-optimistic-blue hover:!bg-putty h-25 w-25
              rounded-full opacity-100 ring-1 ring-offset-[1px]`}
          ></div>
        </div>
      </div>
    );
  }

  if (colorVariants.length < 1) return null;

  const showSlider = (isDesktop ? colorVariants.length > 6 : colorVariants.length > 5) && condensed;

  return (
    <div className={`${condensed ? 'relative mb-12 flex items-center justify-start pr-30 md:pr-20' : 'pl-2'}`}>
      {showSlider && (
        <button
          data-arrow-right={productId}
          className="absolute right-8 z-10 s:left-auto s:right-0"
          aria-label="Next"
        >
          <AngleRightIcon />
        </button>
      )}
      {showSlider ? (
        <Swiper
          className="!ml-0 h-[29px] !pr-2"
          modules={[Navigation]}
          slidesPerView={'auto'}
          spaceBetween={8}
          navigation={{ nextEl: `[data-arrow-right="${productId}"]` }}
          loop={false}
        >
          {colorVariants.map((variant) => (
            <SwiperSlide
              key={variant.id}
              className="!flex min-h-25 max-w-[25px] items-center justify-center"
            >
              <Swatch
                product={product}
                variant={variant}
                currentVariant={currentVariant}
                productId={productId}
                colorOptionIndex={colorOptionIndex}
                handleVariantChange={handleVariantChange}
                condensed={condensed}
                template={template}
                prefix={prefix}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className={`${condensed ? 'flex gap-8' : '-mt-4 gap-15 p-4'} flex flex-wrap items-center`}>
          {colorVariants.map((variant) => (
            <Fragment key={variant.id}>
              <Swatch
                product={product}
                currentVariant={currentVariant}
                variant={variant}
                productId={productId}
                colorOptionIndex={colorOptionIndex}
                handleVariantChange={handleVariantChange}
                condensed={condensed}
                template={template}
                prefix={prefix}
              />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default Swatches;
